import React, { useCallback, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { LovCatalog } from "../api/contracts";
import {
  PortalDataTable,
  PortalTableColumn,
  StatusPill,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { formatLovOrderingMode, formatLovSelectionType } from "../config/lov";
import { lovCatalogDetailPath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

function EmptyValue() {
  return <span className="cp-empty-value">—</span>;
}

const columns: readonly PortalTableColumn<LovCatalog>[] = [
  {
    key: "name",
    header: "Name",
    initialWidth: 280,
    minWidth: 200,
    render: (catalog) => catalog.name,
  },
  {
    key: "description",
    header: "Description",
    initialWidth: 440,
    minWidth: 260,
    cellClassName: "cp-table-cell--multiline",
    render: (catalog) => catalog.description || <EmptyValue />,
  },
  {
    key: "selectionType",
    header: "Selection Type",
    initialWidth: 170,
    minWidth: 140,
    render: (catalog) => formatLovSelectionType(catalog.selectionType),
  },
  {
    key: "status",
    header: "Status",
    initialWidth: 130,
    minWidth: 110,
    resizable: false,
    render: (catalog) => (
      <StatusPill status={catalog.enabled ? "Active" : "Inactive"} />
    ),
  },
  {
    key: "orderingMode",
    header: "Ordering",
    initialWidth: 150,
    minWidth: 125,
    render: (catalog) => formatLovOrderingMode(catalog.orderingMode),
  },
];

export function LovCatalogsPage({ clinicianCode }: { clinicianCode: string }) {
  const [search, setSearch] = useState("");
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getLovCatalogs(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "admin" &&
      data.patient === null,
  );
  const catalogs = useMemo(
    () => [...(data?.catalogs ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [data?.catalogs],
  );
  const filteredCatalogs = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return catalogs;

    return catalogs.filter((catalog) =>
      [
        catalog.name,
        catalog.description,
        formatLovSelectionType(catalog.selectionType),
        catalog.enabled ? "active" : "inactive",
        formatLovOrderingMode(catalog.orderingMode),
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query),
    );
  }, [catalogs, search]);
  const resultCount = search.trim()
    ? `${filteredCatalogs.length} of ${catalogs.length} ${
        catalogs.length === 1 ? "list" : "lists"
      }`
    : `${catalogs.length} ${catalogs.length === 1 ? "list" : "lists"}`;

  return (
    <ClinicianPortalLayout
      activeGlobalKey="lists-of-values"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Lists of Values"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[{ label: "Administration" }, { label: "Lists of Values" }]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        {state.status === "loading" ? (
          <LoadingState label="Loading Lists of Values" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Lists of Values are unavailable")} />
        ) : null}
        {available && data ? (
          <section aria-label="Lists of Values">
            <div className="cp-content-header">
              <div>
                <p>Review configurable value catalogs used by the portal.</p>
              </div>
              <strong className="cp-record-count">{resultCount}</strong>
            </div>
            <div className="cp-list-toolbar">
              <div className="cp-list-toolbar__filters">
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">
                    Search Lists of Values
                  </span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search Lists of Values"
                    onChange={(event) => setSearch(event.currentTarget.value)}
                  />
                </label>
              </div>
            </div>
            {filteredCatalogs.length ? (
              <PortalDataTable
                caption="Lists of Values"
                columns={columns}
                records={filteredCatalogs}
                getRecordKey={(catalog) => catalog.id}
                getRecordOpenLabel={(catalog) => `Open ${catalog.name}`}
                onRecordOpen={(catalog) =>
                  navigateWithinPortal(
                    withClinicianCode(
                      lovCatalogDetailPath(catalog.key),
                      clinicianCode,
                    ),
                  )
                }
              />
            ) : catalogs.length ? (
              <EmptyState
                title="No Lists of Values match this search."
                description="Try a different catalog detail."
              />
            ) : (
              <EmptyState
                title="No Lists of Values found."
                description="No value catalogs are currently available."
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
